import { mdiAccountMultiple, mdiAccountOutline, mdiCogOutline, mdiDnsOutline } from '@mdi/js'
import { ref, watch, getCurrentInstance } from '@vue/composition-api'
import { useActions } from 'vuex-composition-helpers'
import {
  areQueriesEqual,
  didSearchOrFilterValuesChange,
  parseBooleanFromQuery,
  parseFilterArrayFromQuery,
  parseSearchFromQuery,
  parseTableOptionsFromQuery,
  readListQueryFromSession,
  resetTablePageOnFilterChange,
  shouldResetTablePageAfterFetch,
  serializeBooleanToQuery,
  serializeFilterArrayToQuery,
  serializeSearchToQuery,
  serializeTableOptionsToQuery,
  shouldRestoreListStateFromUrlQuery,
  writeListQueryToSession,
} from '@/utils/listRouteState'

const LIST_ROUTE_NAME = 'apps-houses-list'

export default function useHouseList() {
  const vm = getCurrentInstance().proxy
  const houses = ref([])
  const choices = ref({})

  const statusesFilter = ref([])
  const tagsFilter = ref([])
  const hideFullHouses = ref(false)

  const tableColumns = [
    { text: 'name', value: 'name', class: 'ps-7 text-uppercase' },
    { text: 'status', value: 'status', class: 'text-uppercase' },
    { text: 'tags', value: 'tags', class: 'text-uppercase', sortable: false },
    { text: 'address', value: 'address', class: 'text-uppercase', sortable: false },
    { text: 'rooms', value: 'roomCounter', align: 'center', class: 'text-uppercase' },
    { text: 'freeSeats', value: 'freeSeats', align: 'center', class: 'text-uppercase', sortable: false },
    { text: 'accommodationDemand', value: 'demandSum', align: 'center', class: 'text-uppercase', sortable: false },
    {
      text: 'actions',
      value: 'actions',
      align: 'center',
      sortable: false,
      class: 'text-uppercase',
    },
  ]

  const searchQuery = ref('')
  const totalHouseListTable = ref(0)
  const loading = ref(false)
  const options = ref({
    sortDesc: [true],
    page: 1,
    itemsPerPage: 10,
    sortBy: [],
  })
  const userTotalLocal = ref([])
  const overview = ref([])
  const isSyncingFromRoute = ref(false)

  const { getHouses, getHousesFilterChoices, getHousesOverview } = useActions('staff', [
    'getHouses',
    'getHousesFilterChoices',
    'getHousesOverview',
  ])

  const fetchHousesOverview = async () => {
    overview.value = await getHousesOverview()
  }

  const applyStateFromRoute = () => {
    isSyncingFromRoute.value = true

    let query = { ...vm.$route.query }
    if (shouldRestoreListStateFromUrlQuery(query)) {
      const stored = readListQueryFromSession(LIST_ROUTE_NAME)
      if (stored) {
        query = { ...stored }
      }
    }

    statusesFilter.value = parseFilterArrayFromQuery(query, 'status')
    tagsFilter.value = parseFilterArrayFromQuery(query, 'tags')
    hideFullHouses.value = parseBooleanFromQuery(query, 'hideFullHouses', false)
    searchQuery.value = parseSearchFromQuery(query)
    options.value = parseTableOptionsFromQuery(query, options.value)

    isSyncingFromRoute.value = false
    void syncStateToRoute()
  }

  const syncStateToRoute = async () => {
    if (isSyncingFromRoute.value) return

    const nextQuery = {
      ...serializeTableOptionsToQuery(options.value),
      ...serializeFilterArrayToQuery('status', statusesFilter.value),
      ...serializeFilterArrayToQuery('tags', tagsFilter.value),
      ...serializeBooleanToQuery('hideFullHouses', hideFullHouses.value),
      ...serializeSearchToQuery(searchQuery.value),
    }

    if (!areQueriesEqual(vm.$route.query, nextQuery)) {
      await vm.$router.replace({ query: nextQuery })
    }
    writeListQueryToSession(LIST_ROUTE_NAME, nextQuery)
  }

  // fetch data
  const fetchHouses = async (isRetry = false) => {
    loading.value = true
    try {
      const { data, success } = await getHouses({
        status: statusesFilter.value,
        tags: tagsFilter.value,
        hideFullHouses: hideFullHouses.value,
        search: searchQuery.value,
        page: options.value.page,
        pageSize: options.value.itemsPerPage,
        ordering: options.value.sortBy.map((el, index) => {
          const parsedEl = el
            .replaceAll('.', '__')
            .replace('roomCounter', 'room_counter')
            .replace('freeSeats', 'free_seats')

          return `${options.value.sortDesc[index] ? '-' : ''}${parsedEl}`
        }),
      })
      let total = 0
      if (success) {
        if (Object.hasOwnProperty.bind(data)('results') && Object.hasOwnProperty.bind(data)('count')) {
          houses.value = data.results
          totalHouseListTable.value = data.count
          total = data.count
        } else {
          houses.value = data
          totalHouseListTable.value = data.length
          total = data.length
        }
      } else {
        houses.value = []
        totalHouseListTable.value = 0
      }
      if (
        !isRetry &&
        shouldResetTablePageAfterFetch({
          success,
          page: options.value.page,
          total,
          pageSize: options.value.itemsPerPage,
        })
      ) {
        options.value = { ...options.value, page: 1 }
        await syncStateToRoute()
        await fetchHouses(true)
        return
      }
      choices.value = await getHousesFilterChoices({
        status: statusesFilter.value,
        tags: tagsFilter.value,
        hideFullHouses: hideFullHouses.value,
        search: searchQuery.value,
      })
    } finally {
      loading.value = false
    }
  }

  const timer = ref(null)
  const delaySearchHandler = async () => {
    clearTimeout(timer.value)
    timer.value = setTimeout(async () => {
      if (options.value.page !== 1) {
        options.value = { ...options.value, page: 1 }
      }
      await syncStateToRoute()
      await fetchHouses()
    }, 1000)
  }

  applyStateFromRoute()

  watch(
    [statusesFilter, tagsFilter, hideFullHouses, options],
    async ([statuses, tags, hideFull], oldValues) => {
      const filterChanged = didSearchOrFilterValuesChange({
        filterArrays: [statuses, tags],
        oldFilterArrays: oldValues ? [oldValues[0], oldValues[1]] : [],
        booleanFilters: [hideFull],
        oldBooleanFilters: oldValues ? [oldValues[2]] : [],
      })
      if (resetTablePageOnFilterChange(options, filterChanged)) {
        await syncStateToRoute()
        return
      }
      await syncStateToRoute()
      await fetchHouses()
    },
  )

  // *===============================================---*
  // *--------- UI ---------------------------------------*
  // *===============================================---*

  const resolveHouseTotalIcon = (title) => {
    if (title === 'total') return { icon: mdiCogOutline, color: 'secondary' }
    if (title === 'rooms') return { icon: mdiDnsOutline, color: 'primary' }
    if (title === 'taken seats') return { icon: mdiAccountOutline, color: 'success' }
    if (title === 'seats counter') return { icon: mdiAccountMultiple, color: 'info' }

    return { icon: mdiAccountOutline, color: 'primary' }
  }

  return {
    fetchHousesOverview,
    fetchHouses,
    resolveHouseTotalIcon,
    delaySearchHandler,

    tableColumns,
    searchQuery,
    totalHouseListTable,
    loading,
    options,
    userTotalLocal,
    overview,
    houses,
    choices,
    statusesFilter,
    tagsFilter,
    hideFullHouses,
  }
}
